import type { AppRouter } from "../../../api/src/trpc/router";
import type { Ref } from "vue";
import superjson from "superjson";
import { createTRPCNuxtClient, httpBatchLink } from "trpc-nuxt/client";
import { AccessTokenStorage } from "~/storages/accessToken";
import { parseFetchCookie } from "~/libs/cookie";
import { loggerLink } from "@trpc/client";
import { appendResponseHeader } from 'h3';
import { tokenRefreshLink } from "~/libs/tokenRefreshLink";
import { errorHandlerLink } from "~/libs/errorHandlerLink";
import { isTokenRefreshNeeded } from "~/libs/isTokenRefreshNeeded";

export default defineNuxtPlugin((nuxtApp) => {
  const { $i18n } = nuxtApp;

  const event = useRequestEvent();
  const runtimeConfig = useRuntimeConfig();

  const appConfig = useAppConfig();
  const { integrations } = appConfig

  const storage = new AccessTokenStorage(event as any);
  const tokenExpiresAt = useCookie<number | null>('tokenExpiresAt');
  const refreshTokenExpiresAt = useCookie<number | null>('refreshTokenExpiresAt');

  const config = {
    baseURL: integrations?.apiUrl || runtimeConfig.public.API_URL,
  };

  const headers = useRequestHeaders(['cookie', 'user-agent', 'x-forwarded-for', 'x-real-ip']);

  const client = createTRPCNuxtClient<AppRouter>({
    links: [
      loggerLink({
        enabled: (_opts) =>
        (process.env.NODE_ENV === 'development' &&
          typeof window !== 'undefined')
        // || (opts.direction === 'down' && opts.result instanceof Error),
      }),
      tokenRefreshLink({
        tokenRefreshNeeded: (op) => {
          return isTokenRefreshNeeded(op.path, tokenExpiresAt.value);
        },
        fetchAccessToken: async () => {
          await performTokenRefresh(config.baseURL, headers, storage, event, tokenExpiresAt, refreshTokenExpiresAt);
        },
      }),
      errorHandlerLink({
        onTokenInvalid: async () => {
          if (import.meta.client) {
            const authStore = useAuthUser();
            const route = useRoute();
            await authStore.logout(route.fullPath);
          }
        },
        onTokenExpired: async () => {
          await performTokenRefresh(config.baseURL, headers, storage, event, tokenExpiresAt, refreshTokenExpiresAt);
        },
      }),
      httpBatchLink({
        transformer: superjson,
        url: `${config.baseURL}`,
        fetch(url, options) {
          const headers = new Headers(options?.headers as HeadersInit);
          headers.set('x-call-context', import.meta.server ? 'ssr' : 'client');
          headers.set('x-app-locale', $i18n.locale.value)
          headers.set('x-app-scope', 'crm')
          if (import.meta.server && event?.node?.req?.headers?.['user-agent']) {
            headers.set('user-agent', event.node.req.headers['user-agent'] as string);
          }
          if (import.meta.server) {
            const forwardedFor = event?.node?.req?.headers?.['x-forwarded-for'] as string | undefined;
            const realIp = event?.node?.req?.headers?.['x-real-ip'] as string | undefined;
            const remoteIp = event?.node?.req?.socket?.remoteAddress;
            const ip = forwardedFor?.split(',')[0]?.trim() || realIp || remoteIp;
            if (ip) headers.set('x-forwarded-for', ip);
          }
          if (import.meta.server && event?.node?.req?.headers?.cookie) {
            headers.set('cookie', event.node.req.headers.cookie);
            return globalThis.fetch(url, { ...options, headers });
          }
          return globalThis.fetch(url, {
            ...options,
            headers,
            credentials: 'include',
          });
        },
        headers() {
          return { ...headers };
        },
      }),
    ],
  });

  return {
    provide: {
      client,
    },
  };
});

async function performTokenRefresh(
  baseURL: string,
  headers: Record<string, string>,
  storage: AccessTokenStorage,
  event: any,
  tokenExpiresAt: Ref<number | null>,
  refreshTokenExpiresAt: Ref<number | null>,
): Promise<string | null> {
  const refreshUrl = `${baseURL}/auth.accessToken`;
  const cookie = headers.cookie || "";

  const refreshResponse = await fetch(refreshUrl, {
    method: "POST",
    body: '{"json":{}}',
    credentials: "include",
    headers: {
      cookie,
      'Content-Type': 'application/json',
    },
  });

  if (import.meta.server) {
    if (!refreshResponse.ok) {
      tokenExpiresAt.value = null;
      refreshTokenExpiresAt.value = null;
      return null;
    }

    const refreshResponseHeaders = refreshResponse.headers.get('Set-Cookie');
    const parsedCookie = parseFetchCookie(refreshResponseHeaders || '');

    const { token, refreshToken } = parsedCookie;
    if (token) {
      storage.setCookie('token', token);
    }
    if (refreshToken) {
      storage.setCookie('refreshToken', refreshToken, true);
    }

    if (token) {
      if (headers.cookie) {
        const parts = headers.cookie.split(';').map(c => c.trim());
        const hasToken = parts.some(part => part.startsWith('token='));
        if (hasToken) {
          headers.cookie = parts
            .map(part => part.startsWith('token=') ? `token=${token}` : part)
            .join('; ');
        } else {
          headers.cookie = [...parts, `token=${token}`].join('; ');
        }
      } else {
        headers.cookie = `token=${token}`;
      }
    }

    const cookies = (refreshResponse.headers.get('set-cookie') || '').split(/,\s*(?=[A-Za-z0-9_\-.]+=)/);
    for (const cookieValue of cookies) {
      appendResponseHeader(event, 'set-cookie', cookieValue);
    }

    // const refreshData = await refreshResponse.clone().json();
    // const expiresAt = refreshData?.result?.data?.json?.expiresAt ?? null;
    // const refreshExpiresAt = refreshData?.result?.data?.json?.refreshTokenExpiresAt ?? null;

    // tokenExpiresAt.value = expiresAt;
    // refreshTokenExpiresAt.value = refreshExpiresAt;
    return token || null;
  } else {
    const refreshData = await refreshResponse.clone().json();

    const token = refreshData?.result?.data?.json?.token;
    // const expiresAt = refreshData?.result?.data?.json?.expiresAt ?? null;
    // const refreshExpiresAt = refreshData?.result?.data?.json?.refreshTokenExpiresAt ?? null;
    const errorCode = refreshData?.error?.json?.data?.errorCode;

    if (errorCode === 'TOKEN_NOT_FOUND' || !refreshResponse.ok) {
      const authStore = useAuthUser();
      const route = useRoute();
      await authStore.logout(route.fullPath);
      return null;
    }

    // tokenExpiresAt.value = expiresAt;
    // refreshTokenExpiresAt.value = refreshExpiresAt;
    return token || null;
  }
}
